import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShieldX, MapPin, Clock, Monitor, Globe, RefreshCw, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccessDeniedPage = () => {
  const [accessInfo, setAccessInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAccessInfo = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/security/access-denied-info`);
        setAccessInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch access info:', error);
        setAccessInfo({
          ip: 'Unknown',
          timestamp: new Date().toISOString(),
          device: 'Unknown Device',
          location: 'Unknown Location'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAccessInfo();
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }) + ' IST';
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Main Card */}
        <Card className="border-0 shadow-2xl bg-slate-800/50 backdrop-blur-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
              <ShieldX className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Access Denied</h1>
            <p className="text-red-100">Your IP address is not authorized to access this portal</p>
          </div>

          <CardContent className="p-8">
            {/* Warning Banner */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-200 font-medium">Security Notice</p>
                <p className="text-amber-200/70 text-sm">
                  This access attempt has been logged for security purposes. If you believe this is an error, 
                  please contact your administrator.
                </p>
              </div>
            </div>

            {/* Access Details */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white mb-4">Access Details</h2>
              
              {/* IP Address */}
              <div className="flex items-center gap-4 p-4 bg-slate-700/50 rounded-xl border border-slate-600/50" data-testid="blocked-ip">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-red-400" />
                </div>
                <div className="flex-1">
                  <p className="text-slate-400 text-sm">Your IP Address</p>
                  <p className="text-white font-mono text-lg">{accessInfo?.ip || 'Unknown'}</p>
                </div>
              </div>

              {/* Timestamp */}
              <div className="flex items-center gap-4 p-4 bg-slate-700/50 rounded-xl border border-slate-600/50" data-testid="block-timestamp">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <p className="text-slate-400 text-sm">Timestamp</p>
                  <p className="text-white">{formatTimestamp(accessInfo?.timestamp)}</p>
                </div>
              </div>

              {/* Device */}
              <div className="flex items-center gap-4 p-4 bg-slate-700/50 rounded-xl border border-slate-600/50" data-testid="block-device">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Monitor className="w-6 h-6 text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="text-slate-400 text-sm">Device</p>
                  <p className="text-white">{accessInfo?.device || 'Unknown Device'}</p>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-center gap-4 p-4 bg-slate-700/50 rounded-xl border border-slate-600/50" data-testid="block-location">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <p className="text-slate-400 text-sm">Location</p>
                  <p className="text-white">{accessInfo?.location || 'Unknown Location'}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={handleRefresh}
                className="flex-1 bg-slate-600 hover:bg-slate-500"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button 
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => window.location.href = 'mailto:admin@omnilinksolutions.net?subject=IP Access Request'}
              >
                Contact Administrator
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Omnilink Solutions CRM • Enterprise Security
        </p>
      </div>
    </div>
  );
};

export default AccessDeniedPage;
